/**
 * USD pricing model (F-285 / F-286, us2 lane)
 *
 * Pure client-side counterpart of LandlordProBackend
 * `landlord-pro/src/config/usd-pricing.js` (F-284). Used by the Texas pricing
 * surfaces: tier cards, the needs-assessment quiz ("determine a suitable
 * price"), and the per-use service strip.
 *
 * The backend catalog (GET /api/usd-catalog/plans + /services) is the source
 * of truth when reachable; these fallbacks keep the public landing usable
 * when it isn't (first paint, API down). If prices change in usd-pricing.js,
 * mirror them here until the catalog call resolves.
 *
 * Kingdom-business pricing principles baked into this model:
 *  - Just weights (Proverbs 11:1)  → one flat, published monthly price.
 *  - Gleaning margin (Lev 19:9-10) → per-use services cost LESS for small
 *    portfolios (the edge of the field left for the gleaner).
 *  - First fruits (Ex 23:19)       → 14-day full-access trial; pay after the
 *    platform has proven itself, not before.
 */

export interface UsdService {
  type: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  planAccess: string;
}

export interface UsdPlan {
  id: string;
  name: string;
  maxUnits: number;
  price: number;
  currency: string;
  productPath?: string;
  serviceCallAllowance?: number;
  contactSales?: boolean;
  unlockedServices?: string[];
}

/**
 * First-fruits trial length in days. Canonical ruling (F-201 dispatch
 * 2026-10-08): trial = 14d everywhere in copy and tests — matching the LP
 * `TRIAL_DURATION_DAYS` default. The FastSpring offer id `first-fruits-15`
 * is a STORE artifact and is deliberately NOT renamed.
 */
export const FIRST_FRUITS_TRIAL_DAYS = 14;

/** Fallback mirror of usd-pricing.js USD_TIERS (v1 defaults hold per user). */
export const USD_FALLBACK_PLANS: UsdPlan[] = [
  { id: 'freemium', name: 'Homestead', maxUnits: 1, price: 0, currency: 'USD' },
  {
    id: 'essentials',
    name: 'Essentials',
    maxUnits: 1,
    price: 99,
    currency: 'USD',
    productPath: 'texas-landlord-pro-essentials',
    serviceCallAllowance: 5,
  },
  {
    id: 'professional',
    name: 'Professional',
    maxUnits: 5,
    price: 199,
    currency: 'USD',
    productPath: 'texas-landlord-pro-professional',
    serviceCallAllowance: 20,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    maxUnits: 9999,
    price: 0,
    currency: 'USD',
    productPath: 'texas-landlord-pro-enterprise',
    contactSales: true,
  },
];

/** Fallback mirror of usd-pricing.js USD_SERVICES. */
export const USD_FALLBACK_SERVICES: UsdService[] = [
  {
    type: 'TENANCY_VALIDATION',
    name: 'Tenancy Validation',
    description: 'Validate lease agreements and tenant details against Texas Property Code',
    price: 49,
    currency: 'USD',
    planAccess: 'essentials',
  },
  {
    type: 'EVICTION_PACK',
    name: 'Eviction Notice Pack',
    description: 'Complete Texas eviction documentation and notice chain pack',
    price: 79,
    currency: 'USD',
    planAccess: 'professional',
  },
  {
    type: 'TENANT_SERVICES',
    name: 'Tenant Services',
    description: 'Tenant management and communication services',
    price: 29,
    currency: 'USD',
    planAccess: 'essentials',
  },
  {
    type: 'COMPLIANCE_PACK',
    name: 'Compliance Pack',
    description: 'Full Texas compliance documentation and checks',
    price: 29,
    currency: 'USD',
    planAccess: 'essentials',
  },
];

/**
 * Gleaning margin (Lev 19:9-10): small portfolios pay LESS per use — the
 * per-use discount scales with portfolio size, not revenue.
 */
export function gleaningDiscountPct(units: number): number {
  if (units <= 5) return 25;
  if (units <= 20) return 10;
  return 0;
}

export function gleaningPrice(basePrice: number, units: number): number {
  const pct = gleaningDiscountPct(units);
  return Math.round((basePrice * (100 - pct)) / 100);
}

// ── Needs-assessment quiz ("a few questions to determine a suitable price") ──

export interface QuizAnswers {
  units: string; // '1' | '2-5' | '6-20' | '21+'
  services: string; // 'compliance' | 'tenant' | 'legal' | 'all'
  evictions: string; // 'none' | 'sometimes' | 'frequent'
}

export interface QuizRecommendation {
  tierId: 'essentials' | 'professional' | 'enterprise';
  tierName: string;
  /** Personalized flat monthly price in USD; null = sales-led. */
  monthlyUsd: number | null;
  /** Seat-equivalents included in the personalized price (6-20 units = 2). */
  seats: number;
  reason: string;
  gleaningPct: number;
  /** Per-use services priced through the gleaning margin for this quiz. */
  perUse: { type: string; name: string; base: number; price: number }[];
  cta: string;
  offerId: 'first-fruits-15' | 'contact-sales';
}

export function maxUnitsOf(unitsAnswer: string): number {
  switch (unitsAnswer) {
    case '1':
      return 1;
    case '2-5':
      return 5;
    case '6-20':
      return 20;
    case '21+':
      return 21;
    default:
      return 1;
  }
}

export function recommendTier(a: QuizAnswers): QuizRecommendation {
  const units = maxUnitsOf(a.units);
  const gleaningPct = gleaningDiscountPct(units);
  const perUse = USD_FALLBACK_SERVICES.map((s) => ({
    type: s.type,
    name: s.name,
    base: s.price,
    price: gleaningPrice(s.price, units),
  }));

  // 21+ units → Enterprise lane (sales-led, multi-city + seats + API).
  if (units >= 21) {
    return {
      tierId: 'enterprise',
      tierName: 'Enterprise',
      monthlyUsd: null,
      seats: 0,
      reason:
        'At 21+ units you need the multi-city dashboard, team seats and API access — that is the Enterprise lane, and pricing is scoped to your portfolio.',
      gleaningPct,
      perUse,
      cta: 'Talk to us',
      offerId: 'contact-sales',
    };
  }

  // Legal/eviction-heavy work needs the Eviction Shield pack → Professional.
  if ((a.services === 'legal' || a.services === 'all') && (a.evictions === 'frequent' || a.services === 'all')) {
    return {
      tierId: 'professional',
      tierName: 'Professional',
      monthlyUsd: 199,
      seats: 1,
      reason:
        'Eviction timelines, legal documents and the full compliance stack fit Professional — with a 20-per-month service allowance and priority support.',
      gleaningPct,
      perUse,
      cta: 'Start Professional',
      offerId: 'first-fruits-15',
    };
  }

  // Everyone else → Essentials. 6-20 units get 2 seat-equivalents at the same
  // published flat price (just weights: no per-unit creep).
  const seats = units >= 6 ? 2 : 1;
  return {
    tierId: 'essentials',
    tierName: 'Essentials',
    monthlyUsd: 99 * seats,
    seats,
    reason:
      seats > 1
        ? 'Compliance tracking across 6–20 units is Essentials with 2 seat-equivalents — one flat price, no per-unit creep.'
        : 'Compliance tracking and tenant basics on a small portfolio fit Essentials — with a 5-per-month service allowance.',
    gleaningPct,
    perUse,
    cta: seats > 1 ? 'Start Essentials (2 seats)' : 'Start Essentials',
    offerId: 'first-fruits-15',
  };
}

export function formatUsd(amount: number): string {
  return `$${amount.toFixed(0)}`;
}

/**
 * Market-facing tier names (human decision 2026-09-27: never show dev-speak
 * like "freemium" on the site). Internal tier ids stay `freemium` to match
 * backend usd-pricing.js; display names are overridden here so BOTH the
 * fallback mirror and the live /api/usd-catalog/plans payload render the
 * Texas-friendly name.
 */
export const TIER_DISPLAY_NAMES: Record<string, string> = {
  freemium: 'Homestead',
};

export function displayTierName(plan: Pick<UsdPlan, 'id' | 'name'>): string {
  return TIER_DISPLAY_NAMES[plan.id] || plan.name;
}
